import apiClient from './client';

export interface WorkshopWithSession {
  // Workshop definition fields
  id: string;
  modality_id: string;
  title: string;
  description: string | null;
  sequence_number: number | null;
  participant_assignment: 'all_participants' | 'subset';
  facilitator_id: string | null;
  facilitator_name?: string;
  facilitator_email?: string;
  attendance_tracking_enabled: boolean;
  session_duration_minutes: number | null;
  created_at: string;
  updated_at: string;
  // Linked session fields
  session_id?: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  zoom_link?: string | null;
  location?: string | null;
  session_status?: string | null;
  attendance_marked?: boolean;
  feedback_submitted?: boolean;
  participant_count?: number;
  // MED-921: finalize/resend gating fields
  finalized_at?: string | null;
  invites_sent_at?: string | null;
  pending_rsvp_count?: number;
}

export interface FinalizeWorkshopPayload {
  video_link?: string;
  location?: string;
}

export interface FinalizeWorkshopResult {
  finalized: boolean;
  attendance_records_created: number;
  calendar_event_created: boolean;
  emails_sent: number;
  errors?: string[];
  // MED-1300: send-outcome fields (AC10 — parity with session sends).
  skipped_participants?: import('../../types').SkippedInviteParticipant[];
  calendar_failures?: import('../../types').InviteCalendarFailure[];
}

export interface ResendWorkshopInvitesPayload {
  video_link?: string;
}

export interface ResendWorkshopInvitesResult {
  invites_sent: number;
  errors?: string[];
  // MED-1300: send-outcome fields (AC9/AC10).
  skipped_participants?: import('../../types').SkippedInviteParticipant[];
  calendar_failures?: import('../../types').InviteCalendarFailure[];
  calendar_event_updated?: boolean;
}

export interface UpdateWorkshopPayload {
  title?: string;
  description?: string | null;
  facilitator_id?: string | null;
  session_duration_minutes?: number | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  zoom_link?: string | null;
  attendance_tracking_enabled?: boolean;
}

export interface WorkshopParticipant {
  attendance_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  rsvp_status: string | null;
  attendance_status: string | null;
  group_id?: string | null;
  group_name?: string | null;
  participant_status: 'active' | 'pending' | 'withdrawn' | null;
  withdrawn_at: string | null;
}

export interface WorkshopEligibleParticipant {
  cohort_participant_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  group_id?: string | null;
  group_name?: string | null;
}

export const workshopsApi = {
  list: async (cohortId: string): Promise<WorkshopWithSession[]> => {
    return apiClient.get<WorkshopWithSession[]>(`/api/cohorts/${cohortId}/workshops`);
  },

  update: async (cohortId: string, workshopId: string, data: UpdateWorkshopPayload): Promise<WorkshopWithSession> => {
    return apiClient.patch<WorkshopWithSession>(`/api/cohorts/${cohortId}/workshops/${workshopId}`, data);
  },

  listParticipants: async (cohortId: string, workshopId: string): Promise<WorkshopParticipant[]> => {
    return apiClient.get<WorkshopParticipant[]>(`/api/cohorts/${cohortId}/workshops/${workshopId}/participants`);
  },

  listEligibleParticipants: async (cohortId: string, workshopId: string): Promise<WorkshopEligibleParticipant[]> => {
    return apiClient.get<WorkshopEligibleParticipant[]>(`/api/cohorts/${cohortId}/workshops/${workshopId}/participants/eligible`);
  },

  addParticipants: async (cohortId: string, workshopId: string, userIds: string[]): Promise<{ added: number }> => {
    return apiClient.post<{ added: number }>(`/api/cohorts/${cohortId}/workshops/${workshopId}/participants`, { user_ids: userIds });
  },

  removeParticipant: async (cohortId: string, workshopId: string, userId: string): Promise<{ removed: boolean }> => {
    return apiClient.delete<{ removed: boolean }>(`/api/cohorts/${cohortId}/workshops/${workshopId}/participants/${userId}`);
  },

  /**
   * MED-921: Finalize a workshop and send Google Calendar invites to its
   * configured participants and facilitator.
   */
  finalize: async (
    cohortId: string,
    workshopId: string,
    payload: FinalizeWorkshopPayload = {},
  ): Promise<FinalizeWorkshopResult> => {
    return apiClient.post<FinalizeWorkshopResult>(
      `/api/cohorts/${cohortId}/workshops/${workshopId}/finalize`,
      payload,
    );
  },

  /**
   * MED-921: Resend calendar invites for an already-finalized workshop.
   * Surfaced when the workshop is finalized AND at least one attendee still
   * has a pending RSVP.
   */
  resendInvites: async (
    cohortId: string,
    workshopId: string,
    payload: ResendWorkshopInvitesPayload = {},
  ): Promise<ResendWorkshopInvitesResult> => {
    return apiClient.post<ResendWorkshopInvitesResult>(
      `/api/cohorts/${cohortId}/workshops/${workshopId}/resend-invites`,
      payload,
    );
  },
};

export default workshopsApi;
